import React, { useEffect, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { AvatarCropper } from '../ui/avatar-cropper';
import { supabase } from '../../lib/supabase';
import { Evaluator } from '../../lib/supabase';
import { UNIVERSITIES } from '../../lib/constants';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export default function EvaluatorProfile() {
  const [evaluator, setEvaluator] = useState<Evaluator | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '' });
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [wasAdminMode, setWasAdminMode] = useState(false);

  const [editData, setEditData] = useState({
    name: '',

    university: '',
    affiliation: '',
    department: '',
    is_grad_student: false,
    grad_program: '',
    grad_year: ''
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled || !user) return;
        const { data, error } = await supabase
          .from('evaluators')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        if (!data) return;
        setEvaluator(data as Evaluator);
        setEditData({
          name: (data as any).name || '',

          university: (data as any).university || '',
          affiliation: (data as any).affiliation || '',
          department: (data as any).department || '',
          is_grad_student: !!(data as any).is_grad_student,
          grad_program: (data as any).grad_program || '',
          grad_year: (data as any).grad_year ? String((data as any).grad_year) : ''
        });
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Admin flag and previous mode flag must be set via hook at top-level
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (user) {
        const { data: adminRow } = await supabase.from('admin_users').select('user_id').eq('user_id', user.id).maybeSingle();
        if (cancelled) return;
        setIsAdmin(!!adminRow);
      }
      try { if (!cancelled) setWasAdminMode(!!localStorage.getItem('was_admin_mode')); } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!evaluator) return;
    if (!editData.name.trim() || !editData.university || !editData.affiliation.trim()) {
      setError('名前と所属は必須です');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('evaluators')
        .update({
          name: editData.name.trim(),

          university: editData.university,
          affiliation: editData.affiliation.trim(),
          department: editData.department.trim() || null,
          is_grad_student: !!editData.is_grad_student,
          grad_program: editData.grad_program || null,
          grad_year: editData.grad_year ? parseInt(editData.grad_year) : null
        })
        .eq('id', evaluator.id);
      if (error) throw error;
      // Sync name to candidates table if exists
      if (evaluator?.email) {
        await supabase.from('candidates').update({
          name: editData.name.trim(),

        }).eq('email', evaluator.email);
      }
      await loadEvaluator();
      setIsEditing(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const [avatarUploading, setAvatarUploading] = useState(false);

  const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  };

  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const handleAvatarSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) { setError('画像ファイルを選択してください'); return; }
    const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
    if (file.size > MAX_AVATAR_BYTES) { setError('画像サイズが大きすぎます (上限 10MB)'); return; }
    const url = URL.createObjectURL(file);
    setCropImageSrc(url);
  };

  const handleAvatarCropDone = async (blob: Blob) => {
    setCropImageSrc(null);
    if (!evaluator) return;
    setAvatarUploading(true);
    setError(null);
    try {
      const fileName = `${evaluator.id}/avatar.jpg`;
      await supabase.storage.from('avatars').upload(fileName, blob, { upsert: true });
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;
      await supabase.from('evaluators').update({ avatar_url: avatarUrl }).eq('id', evaluator.id);
      setEvaluator({ ...evaluator, avatar_url: avatarUrl });
    } catch (err: any) { setError(err.message); }
    finally { setAvatarUploading(false); }
  };

  const handleDeleteAvatar = async () => {
    if (!evaluator) return;
    try {
      await supabase.storage.from('avatars').remove([`${evaluator.id}/avatar.jpg`]);
      await supabase.from('evaluators').update({ avatar_url: null }).eq('id', evaluator.id);
      setEvaluator({ ...evaluator, avatar_url: undefined });
    } catch (err: any) { setError(err.message); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.error || '削除に失敗しました');
      }
      await supabase.auth.signOut();
      window.location.reload();
    } catch (e: any) {
      setError(e.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {cropImageSrc && (
        <AvatarCropper
          imageSrc={cropImageSrc}
          onCropDone={handleAvatarCropDone}
          onCancel={() => setCropImageSrc(null)}
        />
      )}

      {/* Avatar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {evaluator?.avatar_url ? (
                <img src={evaluator.avatar_url} alt="アバター" className="w-16 h-16 rounded-lg object-cover border-2 border-border" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-xl font-bold text-muted-foreground border-2 border-border">
                  {evaluator?.name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="font-medium">{evaluator?.name}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <Upload className="h-3 w-3 mr-1" />
                    {evaluator?.avatar_url ? '変更' : 'アバター設定'}
                    <input type="file" accept="image/*" onChange={(e) => { handleAvatarSelect(e.target.files); e.target.value = ''; }} className="hidden" disabled={avatarUploading} />
                  </label>
                </Button>
                {evaluator?.avatar_url && (
                  <Button variant="ghost" size="sm" onClick={handleDeleteAvatar} className="text-destructive">
                    <Trash2 className="h-3 w-3 mr-1" />
                    削除
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>参加者プロフィール</CardTitle>
            {!isEditing ? (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                編集
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setError(null); }} disabled={saving}>キャンセル</Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? '保存中…' : '保存'}</Button>
              </div>
            )}
          </div>
          <CardDescription>基本情報を編集できます。</CardDescription>
        </CardHeader>
        <CardContent>
          {!isEditing ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                プロフィール情報は折りたたまれています。<span className="hidden md:inline">「編集」を押すとフォームが展開されます。</span>
              </div>
              <div className="text-sm">
                <span className="text-foreground font-medium mr-2">{evaluator?.name}</span>
                <span className="text-muted-foreground">/ {(evaluator as any)?.affiliation || '未設定'}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">名前 *</Label>
                  <Input id="name" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="university">所属 *</Label>
                <select
                  id="university"
                  required
                  className="w-full border rounded-md h-9 px-3 bg-background text-sm"
                  value={editData.university}
                  onChange={(e) => {
                    const id = e.target.value;
                    const uni = UNIVERSITIES.find((u) => u.id === id);
                    setEditData({
                      ...editData,
                      university: id,
                      affiliation: uni?.name ?? '',
                    });
                  }}
                >
                  <option value="">大学を選択してください</option>
                  {UNIVERSITIES.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">系・部署・診療科（任意）</Label>
                <Input id="department" value={editData.department} onChange={(e) => setEditData({ ...editData, department: e.target.value })} placeholder="○○学部、○○部門、○○診療科など" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="is-grad" checked={editData.is_grad_student} onCheckedChange={(c) => setEditData({ ...editData, is_grad_student: !!c })} />
                  <Label htmlFor="is-grad" className="text-sm">大学院生（修士/博士）</Label>
                </div>
                {editData.is_grad_student && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="grad-program">課程（博士・修士）</Label>
                      <select id="grad-program" className="w-full border rounded-md h-9 px-3 bg-background" value={editData.grad_program} onChange={(e) => setEditData({ ...editData, grad_program: e.target.value })}>
                        <option value="">選択してください</option>
                        <option value="doctor">博士</option>
                        <option value="master">修士</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grad-year">学年</Label>
                      <Input id="grad-year" type="number" min="1" max="6" value={editData.grad_year} onChange={(e) => setEditData({ ...editData, grad_year: e.target.value })} placeholder="1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>アカウント設定</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setAccountOpen(v => !v)}>
              {accountOpen ? '閉じる' : '開く'}
            </Button>
          </div>
          <CardDescription>メールアドレス・パスワードの変更</CardDescription>
        </CardHeader>
        {accountOpen && (
          <CardContent className="space-y-6">
            {/* Change email */}
            <div className="space-y-2">
              <Label htmlFor="new-email">新しいメールアドレス</Label>
              <Input
                id="new-email"
                type="email"
                placeholder={evaluator?.email || ''}
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ newEmail: e.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    if (!emailForm.newEmail.trim()) return;
                    setError(null);
                    setEmailSaving(true);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const token = session?.access_token;
                      if (!token) throw new Error('セッションが無効です');
                      const resp = await fetch('/api/update-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ newEmail: emailForm.newEmail.trim() })
                      });
                      const j = await resp.json();
                      if (!resp.ok) throw new Error(j.error || 'メール更新に失敗しました');
                      await loadEvaluator();
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setEmailSaving(false);
                    }
                  }}
                  disabled={emailSaving || !emailForm.newEmail.trim()}
                >
                  {emailSaving ? '更新中…' : 'メールを更新'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">注意: メール変更後は再ログインが必要になる場合があります。</p>
            </div>

            {/* Change password */}
            <div className="space-y-2">
              <Label htmlFor="cur-pwd">現在のパスワード</Label>
              <Input id="cur-pwd" type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} />
              <Label htmlFor="new-pwd" className="mt-2">新しいパスワード</Label>
              <Input id="new-pwd" type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} />
              <Label htmlFor="confirm-pwd" className="mt-2">新しいパスワード（確認）</Label>
              <Input id="confirm-pwd" type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} />
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={async () => {
                    if (!pwdForm.current || !pwdForm.next || pwdForm.next !== pwdForm.confirm) {
                      setError('パスワードを確認してください');
                      return;
                    }
                    setError(null);
                    setPasswordSaving(true);
                    try {
                      const { data: { user } } = await supabase.auth.getUser();
                      if (!user?.email) throw new Error('ユーザーが見つかりません');
                      const { error: signInErr } = await supabase.auth.signInWithPassword({ email: user.email, password: pwdForm.current });
                      if (signInErr) throw new Error('現在のパスワードが正しくありません');
                      const { error: updErr } = await supabase.auth.updateUser({ password: pwdForm.next });
                      if (updErr) throw updErr;
                      setPwdForm({ current: '', next: '', confirm: '' });
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setPasswordSaving(false);
                    }
                  }}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? '変更中…' : 'パスワードを変更'}
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">アカウントの削除</CardTitle>
          <CardDescription>この操作は取り消せません。参加者プロフィールやコメントなど関連データも削除されます。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!confirmDelete ? (
            <Button variant="destructive" onClick={() => { setConfirmDelete(true); setDeleteEmailInput(''); }}>アカウントを削除する</Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">本当に削除しますか？この操作は元に戻せません。</p>
              <div className="space-y-2">
                <Label htmlFor="delete-confirm-email">確認のため、登録メールアドレスを入力してください</Label>
                <Input
                  id="delete-confirm-email"
                  type="email"
                  autoComplete="off"
                  value={deleteEmailInput}
                  onChange={(e) => setDeleteEmailInput(e.target.value)}
                  placeholder={evaluator?.email || ''}
                  disabled={deleting}
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setConfirmDelete(false); setDeleteEmailInput(''); }} disabled={deleting}>キャンセル</Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting || !evaluator?.email || deleteEmailInput.trim() !== evaluator.email}
                >
                  {deleting ? '削除中…' : '完全に削除する'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {(isAdmin || wasAdminMode) && (
        <Card>
          <CardHeader>
            <CardTitle>管理者メニュー</CardTitle>
            <CardDescription>管理者モードに切り替えると、全ユーザーの編集・削除、評価（いいね/参加希望）調整が可能です。</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {isAdmin && (
                <Button onClick={() => { localStorage.setItem('goto_admin', '1'); window.location.reload(); }}>
                  管理者モードへ
                </Button>
              )}
              {wasAdminMode && (
                <Button variant="ghost" onClick={() => { localStorage.setItem('goto_normal', 'evaluations'); window.location.reload(); }}>
                  通常モードへ
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
